import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Timer } from 'lucide-react';
import { authConfig } from '../config/auth';
import { useAuth } from '../contexts/AuthContext';
import Tooltip from './Tooltip';
import Skeleton from './Skeleton';

interface ReviewTimeData {
  startDate: string;
  endDate: string;
  reviewedCount: number;
  averageReviewMs: number;
  medianReviewMs: number;
}

/** Format an ISO date object to the YYYY-MM-DD value used by <input type="date">. */
function toDateInput(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** Shift a YYYY-MM-DD string by a number of days (UTC). */
function shiftDays(dateStr: string, days: number): string {
  const d = new Date(`${dateStr}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return toDateInput(d);
}

/** Earlier of two YYYY-MM-DD strings (lexicographic works for this format). */
function earlier(a: string, b: string): string {
  return a < b ? a : b;
}

// Keep the selectable span within a year to bound the query the widget issues.
const MAX_RANGE_DAYS = 365;

/**
 * Human-readable duration, e.g. "5 days 3 hours", "4 hours", "45 minutes".
 * Hours is the smallest unit we show, unless the total is under an hour,
 * in which case we fall back to minutes.
 */
function formatDuration(ms: number): string {
  if (!ms || ms <= 0) return '—';

  const totalMinutes = Math.round(ms / 60000);

  // Under an hour → minutes.
  if (totalMinutes < 60) {
    if (totalMinutes < 1) return 'less than a minute';
    return `${totalMinutes} ${totalMinutes === 1 ? 'minute' : 'minutes'}`;
  }

  // An hour or more → days + hours (rounded to the nearest hour).
  const totalHours = Math.round(ms / 3600000);
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;

  const parts: string[] = [];
  if (days) parts.push(`${days} ${days === 1 ? 'day' : 'days'}`);
  if (hours) parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
  return parts.join(' ') || '0 hours';
}

const PRESETS: Array<{ label: string; days: number }> = [
  { label: '7D', days: 7 },
  { label: '30D', days: 30 },
  { label: '90D', days: 90 },
];

export default function ReviewTimeWidget() {
  const { token } = useAuth();

  const today = useMemo(() => new Date(), []);
  const [startDate, setStartDate] = useState(() =>
    toDateInput(new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000))
  );
  const [endDate, setEndDate] = useState(() => toDateInput(today));
  const [data, setData] = useState<ReviewTimeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const activePresetDays = useMemo(() => {
    const start = new Date(`${startDate}T00:00:00Z`).getTime();
    const end = new Date(`${endDate}T00:00:00Z`).getTime();
    if (endDate !== toDateInput(today)) return null;
    const diffDays = Math.round((end - start) / (24 * 60 * 60 * 1000));
    return PRESETS.some((p) => p.days === diffDays) ? diffDays : null;
  }, [startDate, endDate, today]);

  const fetchReviewTime = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ startDate, endDate });
      const response = await fetch(
        `${authConfig.apiUrl}/api/admin/analytics/review-time?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error('Failed to load review time');
      const json = await response.json();
      setData(json);
    } catch (err: any) {
      setError(err.message || 'Failed to load review time');
    } finally {
      setLoading(false);
    }
  }, [token, startDate, endDate]);

  useEffect(() => {
    fetchReviewTime();
  }, [fetchReviewTime]);

  const applyPreset = (days: number) => {
    const end = new Date();
    const start = new Date(end.getTime() - days * 24 * 60 * 60 * 1000);
    setStartDate(toDateInput(start));
    setEndDate(toDateInput(end));
  };

  return (
    <div className="dashboard-widget review-time-widget">
      <div className="widget-header">
        <h3 className="widget-title">
          <span className="widget-title-icon">
            <Timer size={20} color="#00bf62" />
          </span>
          Average Review Time
        </h3>
        <Tooltip content="Average time between an employee submitting content and an admin reviewing it (approving or rejecting), across your whole team. Based on submissions that were submitted within the selected date range — regardless of when they were reviewed." />
      </div>

      {/* Range controls */}
      <div className="review-time-controls">
        <div className="review-time-presets">
          {PRESETS.map((p) => (
            <button
              key={p.label}
              type="button"
              className={`review-time-preset ${activePresetDays === p.days ? 'active' : ''}`}
              onClick={() => applyPreset(p.days)}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="review-time-dates">
          <input
            type="date"
            className="review-time-date-input"
            value={startDate}
            min={shiftDays(endDate, -MAX_RANGE_DAYS)}
            max={endDate}
            onChange={(e) => setStartDate(e.target.value)}
            aria-label="Start date"
          />
          <span className="review-time-date-sep">→</span>
          <input
            type="date"
            className="review-time-date-input"
            value={endDate}
            min={startDate}
            max={earlier(toDateInput(today), shiftDays(startDate, MAX_RANGE_DAYS))}
            onChange={(e) => setEndDate(e.target.value)}
            aria-label="End date"
          />
        </div>
      </div>

      {/* Result */}
      <div className="review-time-result">
        {loading && !data ? (
          // First load: the shape of the result, so the widget keeps its height
          <div aria-busy="true">
            <Skeleton width={160} height={30} radius={8} />
            <Skeleton width={230} height={11} style={{ marginTop: 10 }} />
            <Skeleton width={120} height={11} style={{ marginTop: 8 }} />
          </div>
        ) : error ? (
          <div className="review-time-muted">{error}</div>
        ) : !data || data.reviewedCount === 0 ? (
          <div className="review-time-muted">
            No submissions were reviewed in this date range.
          </div>
        ) : (
          // Changing the range keeps the last result on screen, dimmed, until the new one lands
          <div className={loading ? 'review-time-stale' : 'fade-in'}>
            <div className="review-time-value">{formatDuration(data.averageReviewMs)}</div>
            <div className="review-time-subtitle">
              Average across {data.reviewedCount} reviewed submission
              {data.reviewedCount === 1 ? '' : 's'}
            </div>
            <div className="review-time-secondary">
              Median: <strong>{formatDuration(data.medianReviewMs)}</strong>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
